/**
 * AI Types for STORY-034 to STORY-038
 * IA & Automatisation
 */

// Project types for dynamic questionnaire (BMAD method)
export type ProjectType =
  | 'site_vitrine'
  | 'site_ecommerce'
  | 'application_web'
  | 'application_mobile'
  | 'refonte'
  | 'maintenance'
  | 'consulting'
  | 'autre'

// BMAD Method: Background, Motivation, Aspirations, Decision-making
export interface BMADResponse {
  background: string[] // Current situation, context
  motivation: string[] // Why they need this project
  aspirations: string[] // What they want to achieve
  decisionMaking: string[] // Budget, timeline, decision process
}

// Questionnaire question types
export type QuestionType =
  | 'text'
  | 'textarea'
  | 'single_choice'
  | 'multiple_choice'
  | 'scale'
  | 'budget'
  | 'date'

export interface QuestionOption {
  value: string
  label: string
  description?: string
}

export interface QuestionConfig {
  id: string
  category: keyof BMADResponse
  type: QuestionType
  question: string
  description?: string
  required: boolean
  options?: QuestionOption[]
  placeholder?: string
  minValue?: number
  maxValue?: number
  dependsOn?: {
    questionId: string
    values: string[]
  }
}

export interface QuestionnaireTemplate {
  id: string
  projectType: ProjectType
  name: string
  description: string
  questions: QuestionConfig[]
}

// AI Conversation types
export type ConversationChannel = 'web' | 'whatsapp' | 'email'
export type ConversationStatus = 'active' | 'pending_intervention' | 'resolved' | 'archived'
export type MessageRole = 'user' | 'assistant' | 'system'

export interface AIMessage {
  id: string
  conversationId: string
  role: MessageRole
  content: string
  timestamp: string
  metadata?: {
    questionId?: string
    category?: keyof BMADResponse
    tokens?: number
    model?: string
  }
}

export interface AIConversation {
  id: string
  clientId?: string
  clientName?: string
  clientEmail: string
  channel: ConversationChannel
  status: ConversationStatus
  projectType?: ProjectType
  aiEnabled: boolean
  createdAt: string
  updatedAt: string
  lastMessageAt?: string
  messages: AIMessage[]
  bmadResponses?: Partial<BMADResponse>
  summary?: AISummary
  interventionRequest?: InterventionRequest
}

// AI Summary generated from conversation
export interface AISummary {
  id: string
  conversationId: string
  generatedAt: string
  projectType: ProjectType
  technicalSummary: string
  clientNeeds: string[]
  suggestedSolutions: string[]
  estimatedBudgetRange?: {
    min: number
    max: number
    currency: string
  }
  estimatedTimeline?: {
    min: number
    max: number
    unit: 'days' | 'weeks' | 'months'
  }
  bmadAnalysis: {
    background: string
    motivation: string
    aspirations: string
    decisionMaking: string
  }
  suggestedPrompts: string[]
  confidenceScore: number
}

// Intervention request when AI cannot respond
export interface InterventionRequest {
  id: string
  conversationId: string
  createdAt: string
  reason: InterventionReason
  context: string
  lastUserMessage: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  assignedAdminId?: string
  status: 'pending' | 'in_progress' | 'resolved' | 'dismissed'
  resolvedAt?: string
  adminNotes?: string
}

export type InterventionReason =
  // AI intervention reasons
  | 'complex_technical_question'
  | 'pricing_negotiation'
  | 'complaint'
  | 'urgent_request'
  | 'off_topic'
  | 'explicit_human_request'
  | 'ai_uncertainty'
  | 'sensitive_information'
  // Client request types
  | 'creation_projet'
  | 'autre'
  | 'support'
  | 'bug_report'
  | 'feature_request'

export const INTERVENTION_REASON_LABELS: Record<InterventionReason, string> = {
  // AI intervention reasons
  complex_technical_question: 'Question technique complexe',
  pricing_negotiation: 'Negociation tarifaire',
  complaint: 'Reclamation client',
  urgent_request: 'Demande urgente',
  off_topic: 'Hors sujet',
  explicit_human_request: 'Demande explicite d\'un humain',
  ai_uncertainty: 'Incertitude de l\'IA',
  sensitive_information: 'Information sensible',
  // Client request types
  creation_projet: 'Creation de projet',
  autre: 'Autre demande',
  support: 'Support technique',
  bug_report: 'Rapport de bug',
  feature_request: 'Demande de fonctionnalite',
}

// Gemini API types
export interface GeminiConfig {
  apiKey: string
  model: string
  maxTokens: number
  temperature: number
}

export interface GeminiRequest {
  prompt: string
  context?: string
  conversationHistory?: AIMessage[]
  systemInstruction?: string
}

export interface GeminiResponse {
  text: string
  tokens: {
    input: number
    output: number
    total: number
  }
  finishReason: string
  safetyRatings?: Array<{
    category: string
    probability: string
  }>
}

// Hook state types
export interface AIState {
  conversations: AIConversation[]
  interventions: InterventionRequest[]
  isLoading: boolean
  error: string | null
}

export interface QuestionnaireState {
  currentQuestion: number
  answers: Record<string, string | string[]>
  isComplete: boolean
  projectType: ProjectType | null
}

// Project type labels
export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  site_vitrine: 'Site Vitrine',
  site_ecommerce: 'Site E-commerce',
  application_web: 'Application Web',
  application_mobile: 'Application Mobile',
  refonte: 'Refonte de site',
  maintenance: 'Maintenance',
  consulting: 'Consulting',
  autre: 'Autre projet',
}

// Channel labels and icons
export const CHANNEL_LABELS: Record<ConversationChannel, string> = {
  web: 'Site Web',
  whatsapp: 'WhatsApp',
  email: 'Email',
}

export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
  active: 'Active',
  pending_intervention: 'En attente d\'intervention',
  resolved: 'Resolue',
  archived: 'Archivee',
}
